import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost/backend';

const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Función helper para construir URLs de imágenes
export const getImageUrl = (url) => {
  if (!url) return '';
  // Si ya es una URL absoluta, retornarla tal cual
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Si es una URL relativa que empieza con /backend, construir la URL completa
  if (url.startsWith('/backend/')) {
    const base = baseURL.replace('/backend', '');
    return base + url;
  }
  // Si es una URL relativa sin /backend, agregar el prefijo
  if (url.startsWith('/')) {
    const base = baseURL.replace('/backend', '');
    return base + '/backend' + url;
  }
  // Si no tiene prefijo, agregar /backend/uploads/
  return baseURL.replace('/backend', '') + '/backend/uploads/servicios/' + url;
};

// Interceptor para agregar el token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => {
    // Validate that response is strictly JSON and not a string disguised as JSON (which often happens with PHP errors mixed in)
    if (typeof response.data === 'string') {
      try {
        // Attempt to parse if it's a string, but this is a red flag in many cases
        const parsed = JSON.parse(response.data);
        response.data = parsed;
      } catch (e) {
        // If it can't be parsed, it's likely an HTML error page or raw error string
        console.error('Server Error (Non-JSON response):', response.data);
        return Promise.reject(new Error('Error del servidor: Respuesta inválida (No es JSON)'));
      }
    }

    // Check for "soft" errors where status is 200 but content indicates error (common in some PHP setups)
    if (response.data && response.data.status === 'error') {
      const errorMsg = response.data.message || 'Error desconocido del servidor';
      return Promise.reject(new Error(errorMsg));
    }

    return response;
  },
  (error) => {
    let errorMessage = 'Error de conexión o del servidor';

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const data = error.response.data;
      const status = error.response.status;

      // Try to extract message from standard error formats
      if (typeof data === 'object' && data !== null) {
        errorMessage = data.message || data.error || errorMessage;
      } else if (typeof data === 'string') {
        // If the error data is a string (e.g. HTML error page), try to extract useful info or just give generic error
        // Avoid showing raw HTML to user
        if (data.includes('<html') || data.includes('<b>Warning</b>') || data.includes('<b>Fatal error</b>')) {
          errorMessage = 'Error interno del servidor (comunicar a soporte)';
          console.error('Raw Server Error:', data); // Log true error for devs
        } else {
          errorMessage = data;
        }
      }

      // Handle specific status codes
      if (status === 401 || errorMessage.toLowerCase().includes('no autorizado')) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete api.defaults.headers.common['Authorization'];
          window.dispatchEvent(new Event('clearSession'));
          window.location.href = '/login';
          return Promise.reject(new Error('Sesión expirada'));
        }
      }

      if (status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción';
      }

      if (status === 404) {
        errorMessage = 'Recurso no encontrado';
      }

      if (status >= 500) {
        errorMessage = 'Error interno del servidor';
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = 'No se recibió respuesta del servidor. Verifica tu conexión.';
    } else {
      // Something happened in setting up the request that triggered an Error
      errorMessage = error.message;
    }

    // Standardize the error object passed to catch blocks
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    return Promise.reject(enhancedError);
  }
);

